import { ROLE_IDS, ROLES, isMafiaTeam, type RoleId } from './roles';

export const MIN_PLAYERS = 6;
export const MAX_PLAYERS = 20;

export interface PhaseTimers {
  roleRevealSec: number;
  nightSec: number;
  morningSec: number;
  speechSec: number;
  tieSpeechSec: number;
  votingSec: number;
  lastWordSec: number;
  /** Общее обсуждение после речей, перед голосованием; 0 — без обсуждения. */
  discussionSec: number;
}

export interface GameSettings {
  /** Количество спец-ролей; оставшиеся места заполняются мирными жителями. */
  roles: Partial<Record<Exclude<RoleId, 'CIVILIAN'>, number>>;
  timers: PhaseTimers;
  /** Сколько раз за игру доктор может лечить себя. */
  doctorSelfHeals: number;
  /** Показывать роль игрока всем после его смерти. По умолчанию нет — меньше информации у живых. */
  revealRoleOnDeath: boolean;
  /** Даётся ли последнее слово убитым ночью. */
  nightVictimLastWord: boolean;
  /**
   * Заканчивать ночь досрочно, когда все активные роли сходили.
   * По умолчанию false: длительность ночи сама по себе может выдать, какие роли ещё живы.
   */
  nightEndsEarly: boolean;
  /** Открытое голосование: все видят, кто за кого голосовал. */
  openVoting: boolean;
  /**
   * auto — фазы переключают таймеры; manual — таймеров нет, фазы переключает ведущий
   * (только в игре с ведущим, который не участвует).
   */
  pace: 'auto' | 'manual';
  /** Стол может один раз за игру проголосовать за пропуск голосования. */
  skipVoteOnce: boolean;
}

export const DEFAULT_TIMERS: PhaseTimers = {
  roleRevealSec: 15,
  nightSec: 40,
  morningSec: 8,
  speechSec: 60,
  tieSpeechSec: 30,
  votingSec: 30,
  lastWordSec: 30,
  discussionSec: 60,
};

/**
 * Рекомендуемая раздача под размер стола. Для 15 игроков — клубная:
 * 3 мафии + дон, шериф, доктор, любовница, 8 мирных.
 */
export function defaultRoles(playerCount: number): GameSettings['roles'] {
  const mafiaTotal = playerCount <= 7 ? 2 : playerCount <= 10 ? 3 : playerCount <= 16 ? 4 : 5;
  return {
    DON: 1,
    MAFIA: mafiaTotal - 1,
    SHERIFF: 1,
    DOCTOR: playerCount >= 8 ? 1 : 0,
    LOVER: playerCount >= 10 ? 1 : 0,
    MANIAC: 0,
  };
}

export function defaultSettings(playerCount: number): GameSettings {
  return {
    roles: defaultRoles(playerCount),
    timers: { ...DEFAULT_TIMERS },
    doctorSelfHeals: 1,
    revealRoleOnDeath: false,
    // Убитый мафией права последнего слова не имеет; исключённый голосованием — имеет.
    nightVictimLastWord: false,
    nightEndsEarly: false,
    openVoting: true,
    pace: 'auto',
    skipVoteOnce: true,
  };
}

/** Возвращает список проблем; пустой массив — настройки корректны для данного числа игроков. */
export function validateSettings(settings: GameSettings, playerCount: number): string[] {
  const errors: string[] = [];
  if (playerCount < MIN_PLAYERS || playerCount > MAX_PLAYERS) {
    errors.push(`players must be between ${MIN_PLAYERS} and ${MAX_PLAYERS}`);
  }
  let special = 0;
  let mafia = 0;
  for (const [role, count = 0] of Object.entries(settings.roles) as [RoleId, number | undefined][]) {
    if (!Number.isInteger(count) || count < 0) errors.push(`${role}: count must be a non-negative integer`);
    if (count > ROLES[role].maxCount) errors.push(`${role}: at most ${ROLES[role].maxCount}`);
    special += count;
    if (isMafiaTeam(role)) mafia += count;
  }
  if (mafia < 1) errors.push('at least one mafia member is required');
  if (special > playerCount) errors.push('more special roles than players');
  // Мафия должна быть строго меньше остальных, иначе игра закончится сразу после раздачи.
  if (mafia * 2 >= playerCount) errors.push('mafia must be less than half of the table');
  for (const [key, sec] of Object.entries(settings.timers)) {
    // Обсуждение можно выключить нулём, остальные фазы обязательны.
    const min = key === 'discussionSec' ? 0 : 3;
    if (!Number.isFinite(sec) || sec < min || sec > 600) errors.push(`${key}: must be within ${min}..600 seconds`);
  }
  return errors;
}

/** Колода ролей длины playerCount в фиксированном порядке (перемешивается в движке). */
export function buildDeck(settings: GameSettings, playerCount: number): RoleId[] {
  const deck: RoleId[] = [];
  for (const role of ROLE_IDS) {
    if (role === 'CIVILIAN') continue;
    for (let i = 0; i < (settings.roles[role] ?? 0); i++) deck.push(role);
  }
  while (deck.length < playerCount) deck.push('CIVILIAN');
  return deck;
}
